/** Clinician practice roll-up, drill-in, and link composer entry point. */
import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../../lib/supabase/client';
import { labelForGoalStatus, formatShortDate } from '../../lib/effectiveslp/domain';
import { PracticeLinkComposer } from './PracticeLinkComposer';
import { PracticeActivityDetail } from './PracticeActivityDetail';
import { ParentMessagesInbox } from './ParentMessagesInbox';
import { EmptyState, CalendarIllustration } from '../../components/EmptyState';
import { SkeletonBox } from '../../components/Skeleton';

interface InboxThreadSummary {
  threadId: string;
  parentEmail: string | null;
  lastMessageAt: string | null;
  latestPreview: string | null;
  unreadCount: number;
}

interface Goal {
  id: string;
  area: string;
  goal_text: string;
  status: string;
}

interface SparklineBucket {
  goal_id: string;
  week_start: string;
  trials_attempted: number;
  accuracy_pct: number;
}

interface CommonWrongAnswer {
  value: string;
  count: number;
}

interface RollupItem {
  item_id: string;
  position: number;
  target_trials: number | null;
  trials_attempted: number;
  trials_correct: number;
  accuracy_pct: number;
  last_attempted_at: string | null;
  bail_trial_index: number | null;
  common_wrong_answers: CommonWrongAnswer[];
  scoring_rubric?: unknown;
}

interface PerChildRollup {
  child_identifier_hash_prefix: string;
  trials_attempted: number;
  trials_correct: number;
  accuracy_pct: number;
  first_seen_at: string | null;
  last_seen_at: string | null;
  session_count: number;
}

interface PracticeSession {
  id: string;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  trials_attempted: number | null;
  trials_correct: number | null;
  activities_completed: number | null;
  bail_trial_index: number | null;
  device_fingerprint_hash: string | null;
}

interface RollupResponse {
  practice_set_id: string;
  per_item_rollup: RollupItem[];
  per_child_rollup: PerChildRollup[];
  sessions: PracticeSession[];
  sparkline_data: SparklineBucket[];
  goal_texts: Array<{ goal_id: string; goal_text: string; area: string; status: string }>;
}

interface PracticeSet {
  id: string;
  created_at: string;
}

interface Props {
  studentId: string;
  studentName: string;
  goals?: Goal[];
  sessionId?: string | undefined;
}

function goalIdFromRubric(rubric: unknown): string | null {
  if (rubric == null || typeof rubric !== 'object') return null;
  const obj = rubric as Record<string, unknown>;
  const gid = obj.goal_id ?? obj.goalId;
  if (typeof gid === 'string' && gid.length > 0) return gid;
  return null;
}

function titleFromRubric(rubric: unknown): string | null {
  if (rubric == null || typeof rubric !== 'object') return null;
  const obj = rubric as Record<string, unknown>;
  const t = obj.title;
  if (typeof t === 'string' && t.trim().length > 0) return t.trim();
  return null;
}

function Sparkline({ data, label }: { data: number[]; label?: string }): ReactElement {
  const values = data.length ? data : [0];
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const range = Math.max(max - min, 1);
  const width = 120;
  const height = 60;
  const padding = 4;
  const points = values.map((value, index) => {
    const x = values.length === 1
      ? width / 2
      : padding + (index / (values.length - 1)) * (width - padding * 2);
    const y = height - padding - ((value - min) / range) * (height - padding * 2);
    return `${x},${y}`;
  }).join(' ');
  const last = points.split(' ').at(-1) ?? '0,0';
  const [lastX, lastY] = last.split(',');
  return (
    <div className="sparkline" aria-label={label ?? 'Practice trend'}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <polyline fill="none" stroke="currentColor" strokeWidth="2" points={points} />
        <circle cx={lastX} cy={lastY} r="3" fill="currentColor" />
      </svg>
    </div>
  );
}

function relativeDate(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '—';
  const diff = Date.now() - ms;
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return formatShortDate(iso);
}

function latestAttemptForGoal(
  goalId: string,
  items: RollupItem[],
): string | null {
  let latest: string | null = null;
  for (const item of items) {
    const itemGoalId = goalIdFromRubric(item.scoring_rubric);
    if (itemGoalId !== goalId) continue;
    if (item.last_attempted_at && (latest == null || item.last_attempted_at > latest)) {
      latest = item.last_attempted_at;
    }
  }
  return latest;
}

export function PracticeRollupView({
  studentId,
  studentName,
  goals: goalsProp,
  sessionId,
}: Props): ReactElement {
  const [practiceSets, setPracticeSets] = useState<PracticeSet[]>([]);
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [rollup, setRollup] = useState<RollupResponse | null>(null);
  const [goals, setGoals] = useState<Goal[]>(goalsProp ?? []);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<RollupItem | null>(null);
  const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set());
  const [inboxThreads, setInboxThreads] = useState<InboxThreadSummary[]>([]);
  const [inboxBump, setInboxBump] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const [{ data: setsData, error: setsErr }, { data: goalsData }] = await Promise.all([
          supabase
            .from('practice_sets')
            .select('id, created_at')
            .eq('student_id', studentId)
            .order('created_at', { ascending: false }),
          goalsProp
            ? Promise.resolve({ data: goalsProp })
            : supabase
                .from('goals')
                .select('id, area, goal_text, status')
                .eq('student_id', studentId)
                .eq('status', 'active'),
        ]);
        if (cancelled) return;
        if (setsErr) {
          setLoading(false);
          return;
        }
        const sets = (setsData ?? []) as PracticeSet[];
        setPracticeSets(sets);
        if (goalsData && !goalsProp) {
          setGoals(goalsData as Goal[]);
        }
        const latestSetId = selectedSetId ?? (sets[0]?.id ?? null);
        if (latestSetId) {
          const { data, error } = await supabase.functions.invoke<RollupResponse>(
            'api-get-practice-rollup',
            { body: { practice_set_id: latestSetId } },
          );
          if (!cancelled && !error) {
            setRollup(data ?? null);
          }
        }

        // Inbox summary: one row per thread with unread count + latest preview.
        // Drives the unread badge + latest-message preview on the Practice tab.
        const setIds = sets.map((s) => s.id);
        if (setIds.length === 0) {
          if (!cancelled) setInboxThreads([]);
        } else {
          const { data: threadRows, error: threadsErr } = await supabase
            .from('parent_message_threads')
            .select(
              'id, practice_set_id, parent_email_pending, last_message_at, clinician_user_id',
            )
            .in('practice_set_id', setIds)
            .order('last_message_at', { ascending: false, nullsFirst: false });
          if (cancelled) return;
          if (threadsErr || !threadRows) {
            if (!cancelled) setInboxThreads([]);
            return;
          }
          const threadIds = (threadRows as Array<{ id: string }>).map((t) => t.id);
          if (threadIds.length === 0) {
            if (!cancelled) setInboxThreads([]);
            return;
          }
          const { data: msgRows, error: msgErr } = await supabase
            .from('parent_messages')
            .select(
              'id, thread_id, body, sent_at, read_by_clinician_at, sender_user_id',
            )
            .in('thread_id', threadIds)
            .order('sent_at', { ascending: false });
          if (cancelled) return;
          if (msgErr || !msgRows) {
            if (!cancelled) setInboxThreads([]);
            return;
          }
          const summaries: InboxThreadSummary[] = (
            threadRows as Array<{
              id: string;
              parent_email_pending: string | null;
              last_message_at: string | null;
              clinician_user_id: string | null;
            }>
          ).map((t) => {
            const msgs = (msgRows as Array<{
              thread_id: string;
              body: string;
              read_by_clinician_at: string | null;
              sender_user_id: string;
            }>).filter((m) => m.thread_id === t.id);
            const latest = msgs[0];
            // Unread = parent-sent AND not yet read by the clinician.
            // We exclude clinician-sent rows defensively (#307): the
            // server now stamps read_by_clinician_at on clinician-sent
            // inserts, but pre-fix rows from before that change would
            // otherwise keep the Practice-tab badge stuck.
            const unreadCount = msgs.filter(
              (m) =>
                m.sender_user_id !== t.clinician_user_id &&
                !m.read_by_clinician_at,
            ).length;
            return {
              threadId: t.id,
              parentEmail: t.parent_email_pending,
              lastMessageAt: t.last_message_at,
              latestPreview: latest ? latest.body.slice(0, 80) : null,
              unreadCount,
            };
          });
          if (!cancelled) setInboxThreads(summaries);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, selectedSetId, goalsProp, inboxBump]);

  const activeGoals = useMemo(
    () => goals.filter((g) => g.status === 'active'),
    [goals],
  );

  const goalSparklines = useMemo(() => {
    const byGoal: Record<
      string,
      { trials: number[]; accuracy: number[]; weeks: string[] }
    > = {};
    for (const bucket of rollup?.sparkline_data ?? []) {
      const entry = byGoal[bucket.goal_id] ?? {
        trials: [],
        accuracy: [],
        weeks: [],
      };
      entry.trials.push(bucket.trials_attempted);
      entry.accuracy.push(bucket.accuracy_pct);
      entry.weeks.push(bucket.week_start);
      byGoal[bucket.goal_id] = entry;
    }
    return byGoal;
  }, [rollup]);

  const practiceSetIdsForInbox = useMemo(
    () => practiceSets.map((s) => s.id),
    [practiceSets],
  );

  const totalUnread = useMemo(
    () => inboxThreads.reduce((sum, t) => sum + t.unreadCount, 0),
    [inboxThreads],
  );

  const latestInboxPreview = useMemo(
    () => inboxThreads.find((t) => t.latestPreview != null) ?? null,
    [inboxThreads],
  );

  if (composerOpen) {
    return (
      <PracticeLinkComposer
        studentId={studentId}
        sessionId={sessionId}
        onClose={() => setComposerOpen(false)}
      />
    );
  }

  if (inboxOpen) {
    return (
      <ParentMessagesInbox
        studentId={studentId}
        practiceSetIds={practiceSetIdsForInbox}
        onClose={() => setInboxOpen(false)}
        onMarkedRead={() => setInboxBump((b) => b + 1)}
      />
    );
  }

  if (detailItem) {
    const detailTitle =
      titleFromRubric(detailItem.scoring_rubric) ??
      `Activity ${detailItem.position + 1}`;
    return (
      <PracticeActivityDetail
        title={detailTitle}
        item={detailItem}
        studentId={studentId}
        sessions={rollup?.sessions ?? []}
        onBack={() => setDetailItem(null)}
      />
    );
  }

  if (loading) {
    return (
      <section>
        <h2 className="onboarding-eyebrow">Practice</h2>
        <div
          className="skeleton"
          role="status"
          aria-busy="true"
          aria-label="Loading practice"
        >
          <SkeletonBox width="60%" height={20} />
          <SkeletonBox width="80%" height={14} />
          <SkeletonBox width="40%" height={48} style={{ marginTop: 16 }} />
        </div>
      </section>
    );
  }

  const items = rollup?.per_item_rollup ?? [];
  const hasData = items.length > 0;

  return (
    <section>
      <div className="button-row">
        <h2 className="onboarding-eyebrow">Practice</h2>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setComposerOpen(true)}
        >
          Send a practice link
        </button>
      </div>

      {!hasData && practiceSets.length === 0 ? (
        <EmptyState
          illustration={<CalendarIllustration />}
          title={`No practice yet for ${studentName}`}
          body="Send your first practice link and we'll start tracking sessions here."
          cta={{
            label: 'Send a practice link',
            onClick: () => setComposerOpen(true),
          }}
        />
      ) : (
        <>
          {practiceSets.length > 1 && (
            <label>
              Practice set
              <select
                value={selectedSetId ?? practiceSets[0]?.id ?? ''}
                onChange={(e) => setSelectedSetId(e.target.value)}
              >
                {practiceSets.map((s) => (
                  <option key={s.id} value={s.id}>
                    {formatShortDate(s.created_at)}
                  </option>
                ))}
              </select>
            </label>
          )}

          <section>
            <div className="button-row">
              <h3 className="onboarding-eyebrow">Goal trends</h3>
              <button
                type="button"
                className="link-button"
                onClick={() => setInboxOpen(true)}
              >
                Parent messages
                {totalUnread > 0 && (
                  <span className="caseload-chip" aria-label={`${totalUnread} unread`}>
                    {totalUnread}
                  </span>
                )}
              </button>
            </div>
            {latestInboxPreview && (
              <p className="tagline">
                Latest from {latestInboxPreview.parentEmail ?? 'parent'}: "
                {latestInboxPreview.latestPreview}…"
              </p>
            )}
            {activeGoals.length === 0 && (
              <p className="tagline">No active goals for this student.</p>
            )}
            {activeGoals.map((goal) => {
              const sparklines = goalSparklines[goal.id];
              const lastPracticed = latestAttemptForGoal(goal.id, items);
              const isExpanded = expandedGoals.has(goal.id);
              return (
                <div key={goal.id} className="card stack-md">
                  <div className="caseload-row-main">
                    <span className="caseload-row-name">{goal.area}</span>
                    <span className="caseload-chip">
                      {labelForGoalStatus(goal.status)}
                    </span>
                  </div>
                  <p className="tagline">{goal.goal_text}</p>
                  <div className="form-row">
                    <Sparkline
                      data={sparklines?.trials ?? [0, 0, 0, 0]}
                      label="Trials per week"
                    />
                    <Sparkline
                      data={sparklines?.accuracy ?? [0, 0, 0, 0]}
                      label="Accuracy %"
                    />
                  </div>
                  <p className="tagline">
                    Last practiced: {relativeDate(lastPracticed)}
                  </p>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() =>
                      setExpandedGoals((prev) => {
                        const next = new Set(prev);
                        if (next.has(goal.id)) next.delete(goal.id);
                        else next.add(goal.id);
                        return next;
                      })
                    }
                  >
                    {isExpanded ? 'Hide trial history ▴' : 'View trial history ▾'}
                  </button>
                  {isExpanded && (
                    <div className="stack-md">
                      {items.filter(
                        (item) => goalIdFromRubric(item.scoring_rubric) === goal.id,
                      ).length === 0 ? (
                        <p className="tagline">No trial data for this goal yet.</p>
                      ) : (
                        items
                          .filter(
                            (item) =>
                              goalIdFromRubric(item.scoring_rubric) === goal.id,
                          )
                          .map((item) => (
                            <div key={item.item_id} className="card">
                              <span className="caseload-row-name">
                                Activity {item.position + 1}
                              </span>
                              <span className="caseload-row-meta">
                                {item.trials_attempted} trials ·{' '}
                                {item.accuracy_pct}% accuracy
                              </span>
                            </div>
                          ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </section>

          <details>
            <summary>Activity details ▾</summary>
            <div className="stack-md">
              {items.map((item) => {
                const title = titleFromRubric(item.scoring_rubric) ??
                  `Activity ${item.position + 1}`;
                return (
                <button
                  type="button"
                  key={item.item_id}
                  className="caseload-row"
                  onClick={() => setDetailItem(item)}
                >
                  <span className="caseload-row-name">
                    {title}
                  </span>
                  <span className="caseload-row-meta">
                    {item.trials_attempted} trials · {item.accuracy_pct}% accuracy
                    {item.common_wrong_answers[0]
                      ? ` · Common error: ${item.common_wrong_answers[0].value}`
                      : ''}
                    {item.bail_trial_index != null
                      ? ` · Drop-off at trial ${item.bail_trial_index + 1}`
                      : ''}
                  </span>
                </button>
                );
              })}
            </div>
          </details>
        </>
      )}
    </section>
  );
}

export default PracticeRollupView;
